/**
 * MapRenderer — vector map for `{type:"map", route|location|markers}` content.
 *
 * Brain emits `map` items today via the directions/transit synthesizer
 * paths (see synthesizer.py:1012/1022, llm_synthesizer.py:494/603). We
 * accept three shapes:
 *
 *   1. { type: "map", route: { destination?, origin?, polyline?, ... } }
 *   2. { type: "map", location: { lat, lng, name? } }
 *   3. { type: "map", markers: [{ lat, lng, label?, color? }, ...] }
 *
 * Anything else falls back to a Honolulu default — better to draw the
 * basemap so the user sees a map than render nothing at all.
 *
 * MapLibre GL JS + OpenFreeMap (free, no API key, OSM-based) — same
 * stack as wunderguide-web/NearbyMap and per the project memory note.
 */
import React, { useEffect, useRef, useMemo } from 'react';
import maplibregl, { Map as MlMap, Marker, LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { ChatContentItem } from '@wg/types';
import type { Renderer } from '../registry';

// OpenFreeMap ships several styles. Pick by embed color scheme so the
// basemap doesn't sit on a dark partner panel as a glaring white slab.
const OFM_STYLE_LIGHT = 'https://tiles.openfreemap.org/styles/liberty';
const OFM_STYLE_DARK = 'https://tiles.openfreemap.org/styles/dark';
const FALLBACK_CENTER: [number, number] = [-157.8294, 21.2793]; // Honolulu

function pickStyleUrl(): string {
  if (typeof document === 'undefined') return OFM_STYLE_LIGHT;
  // The embed sets --wg-color-scheme on :root via theme injection
  // (themeToCssVars) and stamps data-wg-mode="dark"|"light" on
  // <html> in applyTheme.ts. Either signal works; data-wg-mode is
  // simpler to read than parsing a CSS var.
  const mode = document.documentElement.dataset.wgMode || '';
  return mode === 'dark' ? OFM_STYLE_DARK : OFM_STYLE_LIGHT;
}

interface Point {
  lng: number;
  lat: number;
  label?: string;
  color?: string;
}

function asNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pointFromAny(
  obj: Record<string, unknown> | null | undefined,
): Point | null {
  if (!obj) return null;
  const lat = asNum(obj.lat) ?? asNum(obj.latitude);
  const lng = asNum(obj.lng) ?? asNum(obj.longitude) ?? asNum(obj.lon);
  if (lat == null || lng == null) return null;
  const label =
    (typeof obj.name === 'string' && obj.name) ||
    (typeof obj.label === 'string' && obj.label) ||
    (typeof obj.address === 'string' && obj.address) ||
    undefined;
  return { lat, lng, label };
}

/**
 * Pull a flat list of points from whatever the brain handed us. We don't
 * need to draw the polyline to be useful — just showing pins for origin
 * and destination on a real basemap already beats the prior "no map at
 * all" state.
 */
function extractPoints(item: ChatContentItem): Point[] {
  const out: Point[] = [];
  const anyItem = item as unknown as Record<string, unknown>;

  // Shape 2: top-level location.
  const loc = pointFromAny(anyItem.location as Record<string, unknown>);
  if (loc) out.push(loc);

  // Shape 3: markers array.
  const markers = anyItem.markers;
  if (Array.isArray(markers)) {
    for (const m of markers) {
      const p = pointFromAny(m as Record<string, unknown>);
      if (p) {
        const color = (m as Record<string, unknown>).color;
        if (typeof color === 'string') p.color = color;
        out.push(p);
      }
    }
  }

  // Shape 1: route.{origin,destination}. Route may also nest under .data.
  const route = anyItem.route as Record<string, unknown> | undefined;
  if (route) {
    const data = (route.data as Record<string, unknown>) || route;
    const origin = pointFromAny(data.origin as Record<string, unknown>);
    if (origin) {
      origin.color = '#22c55e'; // green = origin
      out.push(origin);
    }
    const dest = pointFromAny(data.destination as Record<string, unknown>);
    if (dest) {
      dest.color = '#d90091'; // wg magenta = destination
      out.push(dest);
    }
  }

  return out;
}

const MapRenderer: Renderer = ({ item }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<Marker[]>([]);

  // Stabilise points so the marker-sync effect doesn't churn on every render.
  const points = useMemo(() => extractPoints(item), [item]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialCenter: [number, number] =
      points.length > 0 ? [points[0].lng, points[0].lat] : FALLBACK_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: pickStyleUrl(),
      center: initialCenter,
      zoom: 12,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
    mapRef.current = map;
    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // Container is a ref + we only initialise once; deps deliberately empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = points.map(p => {
      const marker = new maplibregl.Marker({ color: p.color ?? '#d90091' })
        .setLngLat([p.lng, p.lat])
        .addTo(map);
      if (p.label) {
        marker.setPopup(new maplibregl.Popup({ offset: 18 }).setText(p.label));
      }
      return marker;
    });

    if (points.length === 0) {
      map.flyTo({ center: FALLBACK_CENTER, zoom: 11, duration: 400 });
      return;
    }
    if (points.length === 1) {
      map.flyTo({
        center: [points[0].lng, points[0].lat],
        zoom: 14,
        duration: 400,
      });
      return;
    }
    const bounds = new LngLatBounds();
    for (const p of points) bounds.extend([p.lng, p.lat]);
    map.fitBounds(bounds, { padding: 36, duration: 500, maxZoom: 15 });
  }, [points]);

  return (
    <div
      className="wg-chat__map"
      ref={containerRef}
      role="img"
      aria-label="Map"
    />
  );
};

export default MapRenderer;
